import { Box, Tooltip } from '@mui/material';
import PropTypes from 'prop-types';
import { useParams, useNavigate } from 'react-router-dom';
import { bookInvoice } from '../../../../../api/invoicing';
import { getInvoiceLog } from '../../../../../redux/slices/invoicing';
import { useDispatch, useSelector } from '../../../../../redux/store';
import { AwaitButton } from '../../../../../reusable-components/await-button';
import Iconify from '../../../../../reusable-components/iconify';
import { useSnackbar } from '../../../../../reusable-components/snackbar';
import { useTabs } from '../../../../../context/TabContext';
import RemoveFromQue from './RemoveFromQue';
import { showDuplicateInvoiceSnackbar } from './notifications/showDuplicateInvoiceSnackbar';
import { useAccountDisabledNotification } from './notifications/AccountDisabledNotification';
import { useMismatchNotification } from './notifications/MismatchNotification';
import {
  useOptimisticSubmitAdd,
  useOptimisticSubmitRemove,
} from '../../../../../context/invoices/InvoiceLiveStatusContext';
import { useInvoiceMismatchChecks } from '../reusable/hooks/useInvoiceMismatchChecks';
import { useInvoiceRefreshOnSubmit } from '../reusable/hooks/useInvoiceRefreshOnSubmit';
import { useCanSubmitInvoice } from '../reusable/hooks/useInvoiceLocks';

SubmitInvoice.propTypes = {
  glRows: PropTypes.array,
  unsavedData: PropTypes.object,
  goToNext: PropTypes.func,
  buttonVariant: PropTypes.string,
  buttonText: PropTypes.string,
  withDuplicateCheck: PropTypes.func,
};
export default function SubmitInvoice({
  glRows,
  unsavedData,
  goToNext,
  buttonVariant = 'contained',
  buttonText = 'Submit Invoice',
  withDuplicateCheck,
}) {
  const { id } = useParams();
  const navigate = useNavigate();

  const dispatch = useDispatch();
  const {
    data: { invoiceItemData },
  } = useSelector((state) => state.invoicing);

  const { invoice } = invoiceItemData || {};
  const { canSubmit, message, isSubmitting: isAlreadySubmitting } = useCanSubmitInvoice(glRows, unsavedData);
  const optimisticAddSubmitting = useOptimisticSubmitAdd();
  const optimisticRemoveSubmitting = useOptimisticSubmitRemove();
  useInvoiceRefreshOnSubmit();
  const mismatchChecks = useInvoiceMismatchChecks();

  const { enqueueSnackbar } = useSnackbar();
  const { addTab } = useTabs();

  const handleSubmit = async (forceThroughDisabledAccount = false) => {
    withDuplicateCheck(async (duplicateTypes = []) => {
      optimisticAddSubmitting(Number(id));
      const response = await bookInvoice(invoice, forceThroughDisabledAccount, duplicateTypes);
      if (response.status !== 200 || !response.data?.success) {
        optimisticRemoveSubmitting(Number(id));
      }

      if (response.status !== 200) {
        enqueueSnackbar('Unable to submit invoice', { variant: 'error' });
        await dispatch(getInvoiceLog(id));
        return;
      }
      if (!response.data.keepFormOpen) {
        enqueueSnackbar(response.data.message, { variant: response.data.success ? 'success' : 'error' });
      } else if (response.data.message?.includes('already assigned to a different invoice')) {
        showDuplicateInvoiceSnackbar(response.data.message, { enqueueSnackbar, addTab, navigate });
      } else if (response.data.message?.includes('Account Disabled')) {
        accountDisabled.open();
      } else {
        enqueueSnackbar(response.data.message, { variant: response.data.success ? 'success' : 'error', persist: true });
      }

      if (!response.data.success) {
        await dispatch(getInvoiceLog(id));
      } else {
        goToNext();
      }
    }, 'Submit');
  };

  const mismatch = useMismatchNotification({ ...mismatchChecks, invoice: invoiceItemData?.invoice }, () =>
    handleSubmit()
  );
  const accountDisabled = useAccountDisabledNotification(() => handleSubmit(true));
  const handleSaveAndSubmit = () => {
    if (mismatch.hasMismatch) mismatch.open();
    else handleSubmit();
  };

  return (
    <>
      {invoiceItemData?.invoice && (
        <Box>
          {invoiceItemData?.invoice?.queInfo === null ? (
            <Tooltip
              title={
                !canSubmit
                  ? message
                  : 'Submits this invoice to the accounting system immediately or queues it based on the system schedule.'
              }
              placement="top"
              arrow
            >
              <Box>
                <AwaitButton
                  variant={buttonVariant}
                  color="secondary"
                  onClick={handleSaveAndSubmit}
                  loading={isAlreadySubmitting}
                  loadingPosition="start"
                  size="small"
                  disabled={!canSubmit}
                  startIcon={<Iconify icon="fa:paper-plane-o" />}
                >
                  {buttonText}
                </AwaitButton>
              </Box>
            </Tooltip>
          ) : (
            <RemoveFromQue />
          )}
        </Box>
      )}
      {mismatch.element}
      {accountDisabled.element}
    </>
  );
}
